import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Upload, Loader2, FileText, CheckCircle, AlertTriangle, Download, RefreshCw } from 'lucide-react';
import { AcceptedInvitesSparkline } from './AcceptedInvitesSparkline';

/**
 * Admin → Members → Batch Upload
 * Upload a CSV to bulk-create admin-activated (silent) member accounts on a
 * chosen tier. Duplicate emails are skipped and reported.
 *
 * Backing endpoint: POST /api/admin/members/batch-upload
 *   FormData: file=<CSV file>, plan_name=<Basic|Professional|Elite|…>
 */
export function AdminBatchUploadMembersPage() {
  const { api } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planName, setPlanName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [referrerCode, setReferrerCode] = useState('');
  const [progress, setProgress] = useState([]);
  const [trend, setTrend] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);

  const fetchProgress = React.useCallback(async () => {
    setProgressLoading(true);
    try {
      const { data } = await api.get('/api/admin/batch-upload/invite-progress?limit=200');
      setProgress(data?.members || []);
      setTrend(data?.trend || []);
    } catch { /* ignore — table hides if empty */ }
    setProgressLoading(false);
  }, [api]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  useEffect(() => {
    let mounted = true;
    api.get('/api/admin/subscription-plans?limit=100').then(({ data }) => {
      if (!mounted) return;
      const active = (data?.plans || []).filter((p) => p.is_active !== false);
      setPlans(active);
      // Default to the lowest-price active tier so the admin doesn't
      // accidentally activate everyone on Elite.
      if (active.length && !planName) {
        const sorted = [...active].sort((a, b) => (a.price || 0) - (b.price || 0));
        setPlanName(sorted[0].name);
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, [api]);

  const downloadTemplate = () => {
    const csv = 'email,name,mobile\njane@example.com,Jane Doe,+639171234567\njohn@example.com,John Smith,+639179876543\n';
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'aga-batch-members-template.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  };

  const upload = async () => {
    if (!file || !planName) {
      toast.error('Choose a plan and a CSV file first.');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('plan_name', planName);
      if (referrerCode.trim()) fd.append('referrer_code', referrerCode.trim().toUpperCase());
      const { data } = await api.post('/api/admin/members/batch-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      toast.success(`Created ${data.created_count} member(s), skipped ${data.skipped_count}.`);
      if (fileRef.current) fileRef.current.value = '';
      setFile(null);
      // Refresh the invite-progress table so admins see the new rows.
      fetchProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch upload failed');
    }
    setBusy(false);
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Batch Upload Members</h1>
          <p className="text-[#A0B4CC] text-sm mt-1">
            Invite people to join AGA on a chosen tier. Each row gets an invitation email
            (sent in daily batches, see Email Health) with a link to set a password and complete
            their profile. No referral bonuses or stock certificate progress are created.
          </p>
        </div>

        <Card className="bg-[#041A3B] border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00E5FF]" />
              1. Prepare your CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-[#A0B4CC] text-xs leading-relaxed">
              Required column: <span className="text-white font-mono">email</span>.
              Optional: <span className="text-white font-mono">name</span>, <span className="text-white font-mono">mobile</span>.
              UTF-8 with BOM is supported. Duplicate emails are skipped.
            </p>
            <Button
              variant="ghost" onClick={downloadTemplate}
              className="text-[#00E5FF] hover:bg-[#00E5FF]/10 border border-[#00E5FF]/30"
              data-testid="batch-download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV template
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-[#041A3B] border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#00E5FF]" />
              2. Choose tier + upload file
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white text-xs uppercase tracking-widest">Assign to plan</Label>
              <select
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                className="mt-1 w-full h-10 rounded-md bg-[#000B18] border border-white/20 text-white px-3 text-sm"
                data-testid="batch-plan-select"
              >
                {plans.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name} — ₱{Number(p.price || 0).toLocaleString()} · {p.duration_days > 0 ? `${p.duration_days} days` : 'lifetime'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-white text-xs uppercase tracking-widest">
                Referrer's referral code <span className="text-[#5C7599] normal-case tracking-normal ml-1">(optional)</span>
              </Label>
              <input
                type="text"
                value={referrerCode}
                onChange={(e) => setReferrerCode(e.target.value.toUpperCase())}
                placeholder="e.g. AGA-4F92"
                className="mt-1 w-full h-10 rounded-md bg-[#000B18] border border-white/20 text-white px-3 text-sm font-mono uppercase tracking-widest"
                data-testid="batch-referrer-code"
              />
              <p className="text-[10px] text-[#5C7599] mt-1">
                Every member in this batch will appear as a Level-1 downline of this referrer. No commissions or stock certificates fire — attribution is display-only.
              </p>
            </div>
            <div>
              <Label className="text-white text-xs uppercase tracking-widest">CSV file</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-sm text-[#A0B4CC] file:bg-[#00E5FF]/15 file:text-[#00E5FF] file:border-0 file:rounded-md file:px-3 file:py-2 file:mr-3 file:cursor-pointer"
                data-testid="batch-file-input"
              />
            </div>
            <Button
              onClick={upload}
              disabled={!file || !planName || busy}
              className="bg-[#00E5FF] text-[#001627] hover:bg-[#00B8D4] w-full sm:w-auto"
              data-testid="batch-upload-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload &amp; send invitations
            </Button>
          </CardContent>
        </Card>

        {result && (
          <Card className="bg-[#041A3B] border-white/10" data-testid="batch-result">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#00E676]" />
                Result
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-md bg-[#000B18]/60 border border-white/5">
                  <p className="text-[10px] uppercase tracking-widest text-[#5C7599]">Rows</p>
                  <p className="text-white font-mono text-lg font-bold">{result.row_count}</p>
                </div>
                <div className="p-3 rounded-md bg-[#00E676]/10 border border-[#00E676]/30">
                  <p className="text-[10px] uppercase tracking-widest text-[#5C7599]">Created</p>
                  <p className="text-[#00E676] font-mono text-lg font-bold">{result.created_count}</p>
                </div>
                <div className="p-3 rounded-md bg-amber-400/10 border border-amber-400/30">
                  <p className="text-[10px] uppercase tracking-widest text-[#5C7599]">Skipped</p>
                  <p className="text-amber-400 font-mono text-lg font-bold">{result.skipped_count}</p>
                </div>
              </div>

              {result.skipped?.length > 0 && (
                <div>
                  <p className="text-white text-sm font-medium mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    Skipped rows
                  </p>
                  <ul className="max-h-60 overflow-y-auto rounded-md border border-white/5 divide-y divide-white/5 text-xs">
                    {result.skipped.map((s, i) => (
                      <li key={`${s.email}-${i}`} className="p-2 flex justify-between gap-3">
                        <span className="text-[#A0B4CC] break-all">{s.email}</span>
                        <span className="text-amber-400 whitespace-nowrap">{s.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.created?.length > 0 && (
                <div>
                  <p className="text-white text-sm font-medium mb-1 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    Created members
                  </p>
                  <ul className="max-h-60 overflow-y-auto rounded-md border border-white/5 divide-y divide-white/5 text-xs">
                    {result.created.map((c, i) => (
                      <li key={`${c.user_id}-${i}`} className="p-2 flex justify-between gap-3">
                        <span className="text-[#A0B4CC] break-all">{c.email}</span>
                        <span className="text-[#00E676] whitespace-nowrap">{c.name || '(unnamed)'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Invite Progress funnel */}
        <Card className="bg-[#041A3B] border-white/10 mt-6" data-testid="batch-invite-progress">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00E5FF]" />
              Invite Progress
            </CardTitle>
            <AcceptedInvitesSparkline trend={trend} />
            <Button variant="ghost" onClick={fetchProgress} disabled={progressLoading}
              className="text-[#00E5FF] hover:bg-[#00E5FF]/10 h-8"
              data-testid="batch-refresh-progress-btn">
              {progressLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </CardHeader>
          <CardContent>
            {progress.length === 0 ? (
              <p className="text-[#5C7599] text-xs text-center py-6">
                No batch-uploaded members yet — upload a CSV above to get started.
              </p>
            ) : (
              <div className="mobile-scroll-table overflow-x-auto rounded-md border border-white/5">
                <table className="w-full text-xs" data-testid="batch-progress-table">
                  <thead className="bg-[#000B18]/60">
                    <tr className="text-[10px] uppercase tracking-widest text-[#5C7599]">
                      <th className="text-left px-3 py-2">Member</th>
                      <th className="text-left px-3 py-2">Plan</th>
                      <th className="text-left px-3 py-2">Delivered</th>
                      <th className="text-left px-3 py-2">Opened</th>
                      <th className="text-left px-3 py-2">Completed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {progress.map((m) => {
                      const dot = (v, color) => (
                        <span title={v ? new Date(v).toLocaleString() : 'Not yet'}
                          className={`inline-block w-2.5 h-2.5 rounded-full ${v ? color : 'bg-[#1A2B47]'}`} />
                      );
                      return (
                        <tr key={m.id} className="hover:bg-white/5" data-testid={`batch-progress-row-${m.id}`}>
                          <td className="px-3 py-2">
                            <p className="text-white truncate max-w-[220px]">{m.name || '(unnamed)'}</p>
                            <p className="text-[#5C7599] text-[10px] break-all">{m.email}</p>
                          </td>
                          <td className="px-3 py-2 text-[#A0B4CC]">{m.plan || '—'}</td>
                          <td className="px-3 py-2">
                            {m.invitation_status === 'pending'
                              ? <span className="text-[10px] text-[#FFEA00]" data-testid={`batch-progress-queued-${m.id}`}>Queued</span>
                              : dot(m.email_delivered_at, 'bg-[#00E5FF]')}
                          </td>
                          <td className="px-3 py-2">{dot(m.opened_at, 'bg-amber-400')}</td>
                          <td className="px-3 py-2">{dot(m.profile_completed_at, 'bg-[#00E676]')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminSidebarLayout>
  );
}

export default AdminBatchUploadMembersPage;
